import { faPlus, faSwatchbook } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@morressier/alembic';
import { useState } from 'react';

import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden
} from '@chakra-ui/react';
import CreateTagCategory, { ITagCategory } from './CreateTagCategory';
import { useQuery } from '@tanstack/react-query';
import { fetchTagCategories } from 'src/api/queries';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import TagCategoryItem from './TagCategoryItem';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

export type ITagCategoryList = ITagCategory & {
  id: string;
  _id: string;
  workspaceId: string;
};

const LOADER_ROWS_COUNT = 2;

/**
 * Column widths are percentages so the table fits its container rather than
 * overflowing into a horizontal scrollbar. Shared with `TagCategoryItem`.
 */
const NAME_COLUMN_WIDTH = '24%';
const DESCRIPTION_COLUMN_WIDTH = '58%';
const COLOR_COLUMN_WIDTH = '12%';
const ACTION_COLUMN_WIDTH = '6%';
const TABLE_COLUMN_WIDTHS = [
  NAME_COLUMN_WIDTH,
  DESCRIPTION_COLUMN_WIDTH,
  COLOR_COLUMN_WIDTH,
  ACTION_COLUMN_WIDTH
];

const ManageTagCategories = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const { workspaceId } = useWorkspace();

  const offset = (page - 1) * pageSize;

  const { data: tagCategories, isFetching: isLoading } = useQuery({
    queryKey: ['tag-category-list', { workspaceId }, page, pageSize],
    queryFn: () =>
      workspaceId ? fetchTagCategories(workspaceId, offset, pageSize) : null,
    keepPreviousData: true,
    onSuccess: data => {
      const nextTotalPages = data?.total ? Math.ceil(data.total / pageSize) : 0;

      if (nextTotalPages > 0 && page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    }
  });

  const totalTagsCategory = tagCategories?.total || 0;
  const startIndex = totalTagsCategory > 0 ? offset + 1 : 0;
  const endIndex = Math.min(
    offset + (tagCategories?.collection.length || 0),
    totalTagsCategory
  );

  const isEmpty = tagCategories?.total === 0;

  const createTagCategoryButton = (
    <Button
      iconLeft={<FontAwesomeIcon icon={faPlus} />}
      onClick={() => setShowCreateModal(true)}
    >
      Create Tag Category
    </Button>
  );

  return (
    <>
      {showCreateModal && (
        <CreateTagCategory
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      <Flex
        alignItems={{ base: 'flex-start', md: 'flex-end' }}
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        width="100%"
        gap={4}
        mb={4}
      >
        {/* Left side — result count */}
        <Box marginBottom={4}>
          {isLoading ? (
            <Text>Searching for matches...</Text>
          ) : (
            <Flex alignItems={'center'} gap={2} height={5}>
              <Text textStyle="textBody">
                {totalTagsCategory > 0 ? (
                  <>
                    Showing <strong>{startIndex}</strong> –{' '}
                    <strong>{endIndex}</strong> of{' '}
                    <strong>{totalTagsCategory}</strong> tag{' '}
                    {totalTagsCategory === 1 ? 'category' : 'categories'}
                  </>
                ) : (
                  <>
                    <strong>0</strong> tag categories
                  </>
                )}
              </Text>
            </Flex>
          )}
        </Box>

        {/* Right side — Button */}
        <Flex alignItems="center" gap={4}>
          {createTagCategoryButton}
        </Flex>
      </Flex>

      <Box width="100%" marginTop={2} overflowX="auto">
        {isEmpty ? (
          <Flex flexDirection={'column'} gap={2} alignItems="center">
            <Flex
              width={'100%'}
              padding={8}
              justifyContent="center"
              background="neutralSecondaryFill"
              borderRadius="lg"
              fontSize="48px"
            >
              <FontAwesomeIcon icon={faSwatchbook} />
            </Flex>
            <Flex
              flexDirection={'column'}
              gap={2}
              alignItems="center"
              marginY={4}
            >
              <Text textStyle="headingGroup">No Tag Category Found</Text>
              <Text textStyle="textBody">
                There are no tag categories available yet. Once categories are
                added, they will appear here.
              </Text>
            </Flex>
            {createTagCategoryButton}
          </Flex>
        ) : (
          <Table size="sm" sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr
                data-testId="tag-categories-table-header"
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black'
                  }
                }}
                background="white"
              >
                <Th p={4} width={NAME_COLUMN_WIDTH}>
                  <Text textStyle="headingItem">Name</Text>
                </Th>
                <Th p={4} width={DESCRIPTION_COLUMN_WIDTH}>
                  <Text textStyle="headingItem">Description</Text>
                </Th>
                <Th p={4} width={COLOR_COLUMN_WIDTH}>
                  <Text textStyle="headingItem">Color</Text>
                </Th>
                <Th p={4} width={ACTION_COLUMN_WIDTH} textAlign="right">
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {!tagCategories
                ? Array.from({ length: LOADER_ROWS_COUNT }).map(
                    (_, rowIndex) => (
                      <Tr
                        key={`tag-categories-loader-row-${rowIndex}`}
                        data-testId="tag-categories-loader-row"
                        background="white"
                      >
                        {TABLE_COLUMN_WIDTHS.map((columnWidth, columnIndex) => (
                          <Td
                            key={`tag-categories-loader-col-${columnIndex}`}
                            width={columnWidth}
                          >
                            <Skeleton h={6} w="100%" />
                          </Td>
                        ))}
                      </Tr>
                    )
                  )
                : tagCategories.collection.map(tagCategory => (
                    <TagCategoryItem key={tagCategory.id} data={tagCategory} />
                  ))}
            </Tbody>
          </Table>
        )}

        <TablePagination
          ariaLabel="Tag categories pagination"
          currentPage={page}
          totalItems={totalTagsCategory}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Box>
    </>
  );
};

export default ManageTagCategories;
